import {
    OrganizationAccessMode,
    OrganizationRoomGrant,
    OrganizationRoomGrantInput,
} from '../../api/organization/organizationApiTypes';

/**
 * The organization page's room assignment editor (option C,
 * docs/plans/coverage-per-room-assignment.md §4.1), as pure functions.
 *
 * One assignment per room: sole or shared use, and the contract that covers it
 * (`spaceAgreementId`) or none — "access only", members book and pay. The draft
 * holds only the rooms that are assigned; a room absent from it is not.
 *
 * The API replaces the organization's whole list on save, so the body is built
 * from the full draft, never from the rooms the operator happened to touch.
 */
export interface AssignmentDraftEntry {
    accessMode: OrganizationAccessMode;
    spaceAgreementId: string | null;
}

export type AssignmentDraft = Record<string, AssignmentDraftEntry>;

/** The draft as the saved state reads it — what "unchanged" is measured against. */
export const draftFromGrants = (grants: OrganizationRoomGrant[]): AssignmentDraft =>
    Object.fromEntries(
        grants.map((grant) => [
            grant.groupId,
            { accessMode: grant.accessMode, spaceAgreementId: grant.spaceAgreementId ?? null },
        ]),
    );

/** The PUT body: every assigned room, in a stable order. */
export const draftToGrantInputs = (draft: AssignmentDraft): OrganizationRoomGrantInput[] =>
    Object.keys(draft)
        .sort()
        .map((groupId) => ({
            groupId,
            accessMode: draft[groupId].accessMode,
            spaceAgreementId: draft[groupId].spaceAgreementId,
        }));

const sameEntry = (a: AssignmentDraftEntry | undefined, b: AssignmentDraftEntry | undefined) =>
    (a?.accessMode ?? null) === (b?.accessMode ?? null)
    && (a?.spaceAgreementId ?? null) === (b?.spaceAgreementId ?? null);

/** Has anything changed against the saved state? Drives the Save button. */
export const isDraftDirty = (draft: AssignmentDraft, saved: AssignmentDraft): boolean => {
    const keys = Array.from(new Set(Object.keys(draft).concat(Object.keys(saved))));
    return keys.some((key) => !sameEntry(draft[key], saved[key]));
};

/**
 * Assign or unassign one room. Re-ticking a room restores what it had when the
 * editor opened (so unticking by mistake does not silently drop its contract
 * link); a room that was not assigned starts shared and access only.
 */
export const toggleRoom = (
    draft: AssignmentDraft,
    groupId: string,
    assigned: boolean,
    saved: AssignmentDraft = {},
): AssignmentDraft => {
    if (!assigned) {
        return Object.fromEntries(Object.entries(draft).filter(([key]) => key !== groupId));
    }
    if (draft[groupId]) return draft;
    return { ...draft, [groupId]: saved[groupId] ?? { accessMode: 'shared', spaceAgreementId: null } };
};

export const updateRoom = (
    draft: AssignmentDraft,
    groupId: string,
    patch: Partial<AssignmentDraftEntry>,
): AssignmentDraft => {
    const current = draft[groupId];
    if (!current) return draft;
    return { ...draft, [groupId]: { ...current, ...patch } };
};

/**
 * "Cover all rooms with contract X": sets the link on every assigned room.
 * Nothing is stored at organization level — the bulk action writes each
 * assignment, so there is no default that could contradict a room (ADR-0008
 * amendment 2026-10-05, decision 6).
 */
export const coverAll = (draft: AssignmentDraft, spaceAgreementId: string): AssignmentDraft =>
    Object.fromEntries(
        Object.entries(draft).map(([groupId, entry]) => [groupId, { ...entry, spaceAgreementId }]),
    );

/**
 * Choose "Zugriff" for one room (ADR-0008 amendment 2026-10-13). "Öffentlich"
 * needs a contract — a public room without one changes nothing — so switching
 * to it on an access-only room picks the organization's contract when it has
 * exactly one; with several, the operator picks and Save waits for it.
 */
export const setAccess = (
    draft: AssignmentDraft,
    groupId: string,
    accessMode: OrganizationAccessMode,
    coveringAgreementIds: string[],
): AssignmentDraft => {
    const current = draft[groupId];
    if (!current) return draft;
    const spaceAgreementId = accessMode === 'public' && !current.spaceAgreementId && coveringAgreementIds.length === 1
        ? coveringAgreementIds[0]
        : current.spaceAgreementId;
    return { ...draft, [groupId]: { accessMode, spaceAgreementId } };
};

/** Rooms set to "Öffentlich" without a contract — Save stays off until fixed. */
export const publicWithoutContract = (draft: AssignmentDraft): string[] =>
    Object.keys(draft).filter((groupId) => draft[groupId].accessMode === 'public' && !draft[groupId].spaceAgreementId);

/**
 * Rooms whose open/closed state this draft may change: newly assigned rooms,
 * and rooms whose "Zugriff" moved between "Öffentlich" and a closing choice.
 * Open/closed is one fact per room, so these are the rooms whose other
 * organizations — and externals — the dialog has to warn about.
 */
export const roomsChangingOpenness = (draft: AssignmentDraft, saved: AssignmentDraft): string[] =>
    Object.keys(draft).filter((groupId) => {
        const before = saved[groupId];
        if (!before) return true;
        return (before.accessMode === 'public') !== (draft[groupId].accessMode === 'public');
    });
